/* eslint-disable object-curly-spacing */
/* eslint-disable no-undef */
/* eslint-disable own-handles */
/* eslint-disable no-innerhtml-assign */
/* eslint-disable consistent-method-names */
/* eslint-disable no-jsdoc */
/**
 * This widget displays a search input control over the map capable of executing
 * search requests and displaying the results in a dropdown under the input.
 *
 * By default the widget collapses when is empty and loses focus.
 *
 * This widget uses the fuzzy search implementation to execute search requests.
 *
 * @example
 * ```javascript
 *  // SearchBox with location button and Polish language
 * var widget = new L.SearchBox({
 *      location: true,
 *      language: 'pl-PL',
 *      view: 'IN'
 *  });
 * widget.addTo(map);
 *
 * // some time later...
 * widget.language('de-DE');
 * widget.view('IN');
 * ```
 *
 * @class SearchBox
 * @namespace L
 * @extends L.Control
 * @uses L.Evented
 * @module Widgets
 * @constructor
 * @param {Object} [options] Options to be passed to the searchBox control
 * @param {String} [options.placeholder="Search for place..."] The placeholder text to be displayed inside
 *     searchBox
 * @param {String} [options.id] Id of a searchBox's DOM element
 * @param {Boolean} [options.collapsed=false] Flag if checkbox is collapsed on initialization
 * @param {Boolean} [options.collapsible=true] Flag if searchBox can be collapsed
 * @param {Boolean} [options.location=false] Display location button to search for current location
 * @param {String} [options.icon="icon-search"] Determines icon of the searchBox
 * @param {Boolean} [options.disablePOIs=false] Determines whether search results should not contain POIs
 * @param {Boolean} [options.viewportBoundingBox=false] Enables or disables searching on a currently visible
 *     map area. If unset, search service will use current map center.
 * @param {Boolean} [options.withPrompt=true] Should widget show user prompts while typing, the call will be
 *     throttled according to the following settings: `minNumberCharsPrompt`, `throttleTimePrompt`, `idleTimeKeyPress`
 * @param {String|Boolean} [options.searchOnDragEnd="always"] This option defines what the widget should do after
 * the user finish dragging the map (and therefore changing the maps's viewport).
 * There are five possible values:
 *  - `"always"`: The widget will repeat the last search everytime the map's viewport changes.
 *  - `"open"`: The last search will be repeated only if the list of results was being displayed
 *            when the map's dragging occurs.
 *  - `"never"`: No action will be taken when the user change the map's viewport.
 *  - `true`: Alias of `"always"`.
 *  - `false`: Alias of `"never"`.
 * @param {Number} [options.minNumberCharsPrompt=3] Sets the minimal required number of characters that are
 *     required to perform search
 * @param {Number} [options.throttleTimePrompt=1000] Sets the minimal time between the following search
 *     service calls when user types text into the input
 * @param {Number} [options.idleTimeKeyPress=200] Sets the time between following key press events to assume
 *     the user finished typing the query
 * @param {String} [options.language] Language code, for example 'en-GB', 'en-US', 'de-DE'. Full list is available
 *     <a href="https://developer.tomtom.com/search-api/search-api-documentation/supported-languages">here</a>.
 * @param {String} [options.view] Sets the geopolitical view used in the search service. Accepted values:
 * 'AR', 'IL', 'MA', 'IN', 'PK' and 'Unified'. Full list is available
 * <a href="https://developer.tomtom.com/search-api/search-api-documentation">here</a>.
 * @param {Object} [options.serviceOptions] Options to be passed to service. See search service options
 * @param {Boolean} [options.imperialDistance=null] Set this option if you want to use a specific unit measurement
 *     system for the new instance of the widget instead of use the default unit
 */
/* jshint nomen:false */
define([
    'display/map',
    'services/search/fuzzySearch',
    'services/reverseGeocode/reverseGeocode',
    'widgets/controls/location',
    'services/utils',
    'unitFormatConverter/unitFormatConverter',
    '../../config/unit/unitTypes'
],
function(L,
    searchService,
    reverseGeocodeService,
    location,
    utils,
    unitFormatConverter,
    unitTypes) {
    'use strict';

    var ENTER = 13,
        ARROWUP = 38,
        ARROWDOWN = 40,
        EVENTS = {
            ResultCreated: 'resultCreated',
            ResultClicked: 'resultClicked',
            ClearButtonClicked: 'clearButtonClicked',
            ResultsCleared: 'resultsCleared',
            ResultsFound: 'resultsFound',
            ResultsDrawn: 'resultsDrawn',
            SearchInputChanged: 'searchInputChanged',
            LocationError: 'locationError'
        };

    /**
     * @param {HTMLElement} element
     * @param {Object} [data]
     * @param {String} [text]
     * @return {Object}
     */
    function searchBoxEvent(element, data, text) {
        return {
            element: element,
            data: data,
            text: text
        };
    }

    function createNoResults(control) {
        var result = L.DomUtil.create('div', 'leaflet-control-search-result', control.resultsContainer);
        result.innerHTML = 'No results found.';
        control.resultsContainer.style.height = 'auto';

        control.fire(control.Events.ResultCreated);
    }

    function locationError(control) {
        return function(error) {
            control.input.value = '';
            control.input.focus();
            control.clearResults();
            createNoResults(control);
            control.fire(control.Events.LocationError, searchBoxEvent(control.input, error, error.message));
            console.error(error);
        };
    }

    function resolveResultText(resultData) {
        if (!resultData || !resultData.address) {
            return resultData;
        }

        return resultData.poi ? resultData.poi.name + ', ' + resultData.address.freeformAddress :
            resultData.address.freeformAddress;
    }

    function locationSuccess(control) {
        return function(location) {
            var options = utils.clone(control.options.serviceOptions);
            reverseGeocodeService(options).position(location.latlng).go(function(result) {
                result.position = location.latlng.lng ? {
                    lat: location.latlng.lat,
                    lon: location.latlng.lng
                } : location.latlng;

                control.clearResults();
                control.input.value = resolveResultText(result);
                control.input.focus();
                control.fire(
                    control.Events.ResultClicked,
                    searchBoxEvent(control.locationIcon, result, control.input.value)
                );
            });
        };
    }

    function createCurrentLocationButton(control) {
        control.location = location({
            size: null
        });
        control.locationIcon = control.location.onAdd(control._map);
        control.inputContainer.appendChild(control.locationIcon);
        control.location.on(control.location.Events.LocationFound, locationSuccess(control));
        control.location.on(control.location.Events.LocationError, locationError(control));
    }

    function isResultsListOpen(control) {
        return control.resultsContainer.hasChildNodes();
    }

    function onMapEndDrag() {
        var control = this,
            options = control.options,
            reRun = false;
        if (control.lastSearchText.trim().length === 0) {
            return;
        }
        if (options.searchOnDragEnd === 'open' && isResultsListOpen(control)) {
            reRun = true;
        } else if (['always', true].indexOf(options.searchOnDragEnd) !== -1) {
            reRun = true;
        }
        if (reRun) {
            control.search(control.input.value);
        }
    }

    function highlightNextResult(keyCode, selectedElement, resultsContainerElement) {
        var highlightClass = 'highlight',
            rules = {
                38: {
                    edgeCase: 'firstChild',
                    nextSibling: 'previousElementSibling',
                    nextAfterEdgeCase: 'lastChild'
                },
                40: {
                    edgeCase: 'lastChild',
                    nextSibling: 'nextElementSibling',
                    nextAfterEdgeCase: 'firstChild'
                }
            },
            newlySelectedElement;
        if (selectedElement) {
            selectedElement.className = selectedElement.className.replace(highlightClass, '');
        }

        if (!selectedElement || selectedElement === resultsContainerElement[rules[keyCode].edgeCase]) {
            newlySelectedElement = resultsContainerElement[rules[keyCode].nextAfterEdgeCase];
        } else {
            newlySelectedElement = selectedElement[rules[keyCode].nextSibling];
        }

        newlySelectedElement.className += ' ' + highlightClass;
    }

    function onResultSelect(resultData) {
        if (!resultData) {
            return;
        }
        this.stopSearch = true;
        this.input.value = resolveResultText(resultData);
        this.clearResultsContainer();
        this.input.blur();
        this.fire(this.Events.ResultClicked, searchBoxEvent(this.input, resultData, this.input.value));
    }

    function createOnKeyDownHandler(searchBoxControl) {
        var searchOnEnterLastTime = 0,
            searchOnEnterLastValue = '',
            timerId;

        return function(event) {
            var code = event.keyCode || event.which,
                selectedResult = this.resultsContainer.getElementsByClassName('highlight')[0],
                selectedResultIndex,
                currentTime,
                currentInputValue,
                throttleTimePrompt,
                deltaTimeToNextSearch;

            if (code === ENTER && searchBoxControl.input.value) {
                if (selectedResult) {
                    selectedResultIndex = [].indexOf.call(this.resultsContainer.children, selectedResult);
                    onResultSelect.call(this, this._searchResponses[selectedResultIndex]);
                } else {
                    if (timerId) {
                        clearTimeout(timerId);
                    }

                    currentTime = new Date().getTime();
                    currentInputValue = searchBoxControl.input.value;
                    throttleTimePrompt = searchBoxControl.options.throttleTimePrompt || 1000;
                    deltaTimeToNextSearch = throttleTimePrompt - (currentTime - searchOnEnterLastTime);

                    if (deltaTimeToNextSearch <= 0 || searchOnEnterLastValue !== currentInputValue) {
                        searchOnEnterLastTime = currentTime;
                        searchOnEnterLastValue = currentInputValue;
                        searchBoxControl.search(currentInputValue);
                    } else {
                        timerId = setTimeout(function() {
                            searchOnEnterLastTime = new Date().getTime();
                            searchOnEnterLastValue = searchBoxControl.input.value;
                            searchBoxControl.search(searchBoxControl.input.value);
                        }, deltaTimeToNextSearch);
                    }
                }
            }

            if (this.resultsContainer.hasChildNodes()) {
                if (code === ARROWUP || code === ARROWDOWN) {
                    this.stopSearch = true;
                    highlightNextResult.call(this, code, selectedResult, this.resultsContainer);
                }
            }
        };
    }

    function getOptionsForSearch(control) {
        var options = utils.clone(control.options.serviceOptions);
        options.query = control.input.value;
        if (control.options.viewportBoundingBox) {
            options.boundingBox = control._map.getBounds();
        } else {
            options.center = control._map.getCenter();
        }
        if (control.options.disablePOIs) {
            options.idxSet = 'PAD,Str,Xstr,Geo,Addr';
        }
        if (typeof options.typeahead !== 'boolean') {
            options.typeahead = true;
        }
        if (control.options.view) {
            options.view = control.options.view;
        }
        if (!utils.isString(options.language)) {
            options.language = control.options.language;
        }
        return options;
    }
    function handleResults(results) {
        this.expand();
        this._searchResponses = results;
        this._redrawResults(true);
        this.fire(this.Events.ResultsFound, searchBoxEvent(this.resultsContainer, results));
    }

    function createOnInputChangeHandler(control) {
        var timerId;

        function promptSearch() {
            if (control.input.value.length === 0 || control.lastSearchText === control.input.value ||
                    control.stopSearch) {
                return;
            }

            control.lastSearchText = control.input.value;

            searchService(getOptionsForSearch(control)).go(function(result) {
                if (!control.stopSearch) {
                    handleResults.call(control, result);
                }
            });
        }

        return function() {
            this.fire(this.Events.SearchInputChanged, searchBoxEvent(this.input, this.input.value, this.input.value));
            control.stopSearch = false;

            if (timerId) {
                clearTimeout(timerId);
            }

            if (this.options.withPrompt && this.input.value.length >= this.options.minNumberCharsPrompt) {
                if (control.options.idleTimeKeyPress === 0) {
                    promptSearch();
                } else {
                    timerId = setTimeout(promptSearch, control.options.idleTimeKeyPress);
                }
            }
        };
    }

    function shouldListenForDragEnd(control) {
        return ['always', 'open', true].indexOf(control.options.searchOnDragEnd) !== -1;
    }

    function clearSearchBox() {
        this.input.focus();
        this.input.value = '';
        this.lastSearchText = '';
        this.clearResults();
        this.fire(this.Events.ClearButtonClicked, searchBoxEvent(this.clearButton));
    }

    function setHandlers(control, container) {
        L.DomEvent.disableClickPropagation(container);
        L.DomEvent.disableScrollPropagation(container);
        L.DomEvent.on(container, 'drag', L.DomEvent.stopPropagation);
        L.DomEvent.on(container, 'keydown', createOnKeyDownHandler(control), control);
        L.DomEvent.on(container, 'input', createOnInputChangeHandler(control), control);

        if (shouldListenForDragEnd(control)) {
            L.DomEvent.on(control._map, 'dragend', onMapEndDrag, control);
        }

        L.DomEvent.on(control.clearButton, 'click', clearSearchBox, control);
        if (control.options.collapsible) {
            L.DomEvent.on(control.input, 'focus', control.expand, control);
            L.DomEvent.on(control.input, 'blur', control.collapse, control);
        }
    }

    function createDOMElements(control) {
        var container, containerClassname = 'leaflet-control-search';
        if (control.options.collapsible && control.options.collapsed) {
            containerClassname += ' collapsed';
        }
        container = L.DomUtil.create('div', containerClassname);
        control.inputContainer = L.DomUtil.create('div', 'leaflet-control-search-input-container', container);
        control.input = L.DomUtil.create(
            'input',
            control.options.icon + ' leaflet-control-search-input',
            control.inputContainer
        );
        control.input.required = true;
        control.clearButton = L.DomUtil.create(
            'div',
            'leaflet-control-search-input-close-icon icon-close-gray',
            control.inputContainer
        );
        control.resultsContainer = L.DomUtil.create(
            'div',
            'leaflet-control-search-results-container',
            container
        );
        if (control.options.location) {
            createCurrentLocationButton(control);
        }
        if (control.options.id) {
            control.input.id = control.options.id;
        }
        setHandlers(control, container);
        control.input.setAttribute('placeholder', control.options.placeholder);
        return container;
    }

    function getWordsToHighlight(words) {
        var i;
        words = words.split(/[- ,]+/) || [];

        for (i = 0; i < words.length; i += 1) {
            words[i] = utils.encodeStringForRegex(words[i]);
        }

        return words;
    }

    function highlightWords(control, text) {
        var words = getWordsToHighlight(control.input.value);
        var regex = new RegExp('(' + words.join('|') + ')', 'ig');

        return text.replace(
            regex, '<span class="leaflet-control-search-result-highlight">$1</span>'
        );
    }

    function createResultElement(control, result) {
        var useImperial = control._shouldUseImperialDistance();
        var nameWrapper, addressWrapper, address, distanceElement;
        var resultElement = L.DomUtil.create('div', 'geoResponse-result');

        if (typeof result.poi !== 'undefined' && typeof result.poi.name !== 'undefined') {
            nameWrapper = L.DomUtil.create('div', 'geoResponse-result-name');
            nameWrapper.innerHTML = highlightWords(control, result.poi.name);
            resultElement.appendChild(nameWrapper);
        }

        if (typeof result.address !== 'undefined') {
            addressWrapper = L.DomUtil.create('div', 'geoResponse-result-address');
            address = [];

            if (typeof result.address.freeformAddress !== 'undefined') {
                address.push(result.address.freeformAddress);
            }

            if (typeof result.address.countryCodeISO3 !== 'undefined') {
                address.push(result.address.countryCodeISO3);
            }

            addressWrapper.innerHTML = highlightWords(control, address.join(', '));
            resultElement.appendChild(addressWrapper);
        }

        if (typeof result.dist !== 'undefined') {
            distanceElement = L.DomUtil.create('div', 'geoResponse-result-distance');
            distanceElement.innerHTML = unitFormatConverter.formatDistance(result.dist, useImperial);
            resultElement.appendChild(distanceElement);
        }

        return resultElement;
    }

    function createResult(control, resultData) {
        var result = L.DomUtil.create('div', 'leaflet-control-search-result', control.resultsContainer);
        result.appendChild(createResultElement(control, resultData));
        control.resultsContainer.style.height = 'auto';
        control.fire(control.Events.ResultCreated, searchBoxEvent(result, resultData));

        L.DomEvent.on(result, 'click', function() {
            onResultSelect.call(control, resultData);
        }, control);

        L.DomEvent.on(result, 'mouseover', function() {
            this.stopSearch = true;
        }, control);
    }

    function handleUnitChange(event) {
        this.options.imperialDistance = event.unit === 'imperial';
        if (isResultsListOpen(this)) {
            this._redrawResults(true);
        }
    }

    function handleLocaleChange(event) {
        if (this.definedLanguage) {
            return;
        }
        this.language(event.getValueFor('search'));
        if (this.lastSearchText && isResultsListOpen(this)) {
            this.search(this.lastSearchText);
        }
    }

    function updateSearch() {
        if (this.lastSearchText && isResultsListOpen(this)) {
            this.search(this.lastSearchText);
        }
    }

    function handleGeopolViewChange(event) {
        if (this.definedGeopolView) {
            return;
        }
        this.options.view = event.code;
        updateSearch.call(this);
    }

    L.SearchBox = L.Control.extend({
        includes: L.Evented.prototype,

        options: {
            placeholder: 'Search for a place...',
            id: false,
            collapsible: true,
            collapsed: false,
            location: false,
            icon: 'icon-search',
            disablePOIs: false,
            viewportBoundingBox: false,
            minNumberCharsPrompt: 3,
            throttleTimePrompt: 1000,
            idleTimeKeyPress: 200,
            serviceOptions: {},
            position: 'topleft',
            withPrompt: true,
            imperialDistance: null,
            searchOnDragEnd: 'always'
        },

        statics: {
            Events: EVENTS
        },

        /**
         * @ignore
         * @type {HtmlElement}
         */
        resultsContainer: null,

        /**
         * @ignore
         * @type {HtmlElement}
         */
        input: null,

        /**
         * @ignore
         * @type {HtmlElement}
         */
        location: undefined,

        /**
         * @ignore
         * @type {HtmlElement}
         */
        locationIcon: null,

        /**
         * @ignore
         * Store the last search results received.
         * @type {Object[]}
         */
        _searchResponses: [],

        /**
         * @ignore
         * @type {String}
         */
        lastSearchText: '',

        initialize: function(options) {
            L.Util.setOptions(this, options);
            if (options) {
                this.definedGeopolView = options.view;
                this.definedLanguage = options.language;
            }
        },

        onAdd: function(map) {
            this.unitService = map.getUnitService();
            this.unitService.on(L.UnitService.Events.UNIT_CHANGED, handleUnitChange, this);
            this.localeService = map.getLocaleService();
            this.localeService.on(L.LocaleService.Events.LOCALE_CHANGED, handleLocaleChange, this);
            this.geopolService = map.getGeopolViewService();
            this.geopolService.on(L.GeopolViewService.Events.GEOPOL_VIEW_CHANGED, handleGeopolViewChange, this);
            return createDOMElements(this);
        },

        onRemove: function() {
            this.unitService.off(L.UnitService.Events.UNIT_CHANGED, handleUnitChange, this);
            this.localeService.off(L.LocaleService.Events.LOCALE_CHANGED, handleLocaleChange, this);
            this.geopolService.off(L.GeopolViewService.Events.GEOPOL_VIEW_CHANGED, handleGeopolViewChange, this);
            if (shouldListenForDragEnd(this)) {
                L.DomEvent.off(this._map, 'dragend', onMapEndDrag, this);
            }
        },

        /**
         * @ignore
         * First check the configuration for this instance and then the global configuration.
         * The instance configuration has priority.
         *
         * @return {Boolean}
         */
        _shouldUseImperialDistance: function() {
            if (typeof this.options.imperialDistance === 'boolean') {
                return this.options.imperialDistance;
            }
            return this.unitService.getCurrentUnit().getLabel() === unitTypes.imperial;
        },

        /**
         * @ignore
         * Show the list of results under the widget.
         * @param {Boolean} [informNoResults=false] If is TRUE shows a notification saying
         *     there's no results no show
         */
        _redrawResults: function(informNoResults) {
            var i, results = this._searchResponses;

            this.clearResultsContainer();

            if (!results || results.length === 0) {
                if (informNoResults === true) {
                    createNoResults(this);
                }
                return;
            }

            for (i = 0; i < results.length; i += 1) {
                createResult(this, results[i]);
            }

            this.resultsContainer.style.height = 'auto';
            this.fire(this.Events.ResultsDrawn, searchBoxEvent(this.resultsContainer, results));
        },

        /**
         * Method used to clear the DOM elements from the search results container.
         *
         * This method just remove the DOM elements but leave the cache with the previous results inside the widget.
         *
         * @method clearResultsContainer
         */
        clearResultsContainer: function() {
            if (this.resultsContainer.hasChildNodes()) {
                this.resultsContainer.style.height = '0px';
                this.resultsContainer.innerHTML = '';
                this.fire(this.Events.ResultsCleared, searchBoxEvent(this.resultsContainer));
            }
        },

        /**
         * Method used to clear all the previous search results from the widget.
         *
         * @method clearResults
         */
        clearResults: function() {
            this.clearResultsContainer();
            this._searchResponses = [];
        },

        /**
         * Method used to feed the searchBox instance with one search result element.
         *
         * @method setResultData
         * @param {Object} resultData Result data given from search service. Must contain address and/or POI
         *     information.
         *
         *  @example
         * ```javascript
         * var widget = (new L.SearchBox()).addTo(map);
         * var resultData = {
         *   address: {freeformAddress: "Amsterdam"},
         *   poi: { name: "TomTom, Oosterdoksstraat, Amsterdam, The Netherlands" }
         * }
         * widget.setResultData(resultData);
         * ```
         */
        setResultData: function(resultData) {
            if (resultData) {
                this.input.value = resolveResultText(resultData);
                this.clearResults();
                this.input.blur();
            }
        },

        /**
         * Start a search using a query string.
         * This method update the content of the input control and also handle the search request.
         *
         * @method search
         * @param {Object|String} text String, Object or reverse geocoding object (with the `query` key) as a search
         *     query
         *
         *  @example
         * ```javascript
         * var widget = new L.SearchBox();
         * widget.addTo(map);
         * widget.search("Amsterdam");
         *
         * var reversedGeocodeResponse = {query: "Amsterdam"}
         * widget.search(reversedGeocodeResponse);
         * ```
        */
        search: function(text) {
            var searchBox = this;
            if (typeof text === 'object' && text.hasOwnProperty('query')) {
                text = text.query;
            }
            if (!utils.isString(text) || text.length === 0) {
                return;
            }
            this.input.focus();
            this.input.value = text;
            this.lastSearchText = text;

            searchService(getOptionsForSearch(searchBox)).go(function(results) {
                handleResults.call(searchBox, results);
            });
        },
        /**
         * Method used to collapse search. Collapsing is possible only if search is empty.
         *
         * @method collapse
         */
        collapse: function() {
            if (!this.input.value) {
                this._container.className += ' collapsed';
                this.clearResultsContainer();
            } else {
                this.expand();
            }
        },
        /**
         * Method used to expand collapsed search.
         *
         * @method expand
         */
        expand: function() {
            this._container.className = this._container.className.replace(/\b collapsed\b/, '');
        },

        /**
         * Method used to get embedded location widget (if set and added).
         *
         * @method getLocation
         * @return {L.Location}
         */
        getLocation: function() {
            return this.location;
        },

        /**
         * Sets the icon by styling search box input.
         *
         * @method setIcon
         * @param {String|undefined} className Name of the CSS class that sets
         *     `background-image` property for search box input
         */
        setIcon: function(className) {
            if (utils.isString(className)) {
                this.input.className = this.input.className.replace(/(icon)((-)(\w+))*/, className);
            }
        },

        /**
         * Adds the location icon.
         *
         * @method addLocationIcon
         */
        addLocationIcon: function() {
            if (this.location === undefined) {
                this.options.location = true;
                createCurrentLocationButton(this);
            }
        },

        /**
         * Removes the location icon.
         *
         * @method removeLocationIcon
         */
        removeLocationIcon: function() {
            if (this.location !== undefined) {
                this.options.location = false;
                this.locationIcon.parentNode.removeChild(this.locationIcon);
                this.location.clearAllEventListeners();
                this.location = undefined;
            }
        },

        /**
         * A setter for the _'language'_ option.
         *
         * The whitelist can be found
         * [here](https://developer.tomtom.com/search-api/search-api-documentation/supported-languages).
         *
         * @method language
         * @param {String} [languageCode] Language code, for example 'en-GB', 'en-US', 'de-DE'. Full list is available
         * <a href="https://developer.tomtom.com/search-api/search-api-documentation/supported-languages">here</a>
         * .
         */
        language: function(languageCode) {
            if (null === languageCode || '' === languageCode) {
                delete this.options.language;
                delete this.definedLanguage;
            } else {
                this.options.language = languageCode;
                this.definedLanguage = languageCode;
            }
        },

        /**
         * A setter for the 'view' option.
         *
         * Use this setter to set a geopolitical view.
         * When this parameter is used the search service will return values
         * taking in consideration the Geopolitical view used.
         *
         * Accepted values: 'AR', 'IL', 'MA', 'IN', 'PK' and 'Unified'
         *
         * @method view
         * @param {String} [geopolViewCode] Geopotical view code. Full list is available at
         * <a href="https://developer.tomtom.com/search-api/search-api-documentation">here</a>
         * .
         */
        view: function(geopolViewCode) {
            if (geopolViewCode === null || geopolViewCode === '') {
                delete this.options.view;
                delete this.definedGeopolView;
            } else {
                this.options.view = geopolViewCode;
                this.definedGeopolView = geopolViewCode;
            }
            updateSearch.call(this);
        },

        /**
         * Whenever the input within the search box is changed by the user this event is triggered.
         *
         * @event SearchInputChanged
         * @param {Object} data The current text entered by the user
         * @param {String} text Same as data
         * @param {String} [type="searchInputChanged"] Event's name
         * @param {Element} target Event's target
         *
         * @example
         * ```javascript
         * var widget = new L.SearchBox();
         * widget.addTo(map);
         * widget.on(L.SearchBox.Events.SearchInputChanged, function(eventObject) {
         *   console.log(eventObject.text);
         * });
         * ```
         */
        /**
         * This event appears when response from search service is being processed. For each result which is being
         * rendered on the prompt list such event is fired.
         *
         * @event ResultCreated
         * @param {Object} data The result from search service response that is being processed
         * @param {String} [type="resultCreated"] Event's name
         * @param {Element} target Event's target
         *
         * @example
         * ```javascript
         * var widget = new L.SearchBox();
         * widget.addTo(map);
         * widget.on(L.SearchBox.Events.ResultCreated, function(eventObject) {
         *   console.log(eventObject.data);
         * });
         * ```
         */
        /**
         * Event that is fired when user selects the result from list or when current location is identified.
         *
         * @event ResultClicked
         * @param {Object} data Selected result object from search service response
         * @param {String} text The human readable string representation of selected result
         * @param {String} [type="resultClicked"] Event's name
         * @param {Element} target Event's target
         *
         * @example
         * ```javascript
         * var widget = new L.SearchBox();
         * widget.addTo(map);
         * widget.on(L.SearchBox.Events.ResultClicked, function(eventObject) {
         *   console.log(eventObject.text);
         *   console.log(eventObject.data);
         * });
         * ```
         */
        /**
         * Event that is fired when the response from search service is already processed and rendered in a form
         * of list of possible results.
         *
         * @event ResultsDrawn
         * @param {Array} data Array of search service results. Each result has, among other parameters, its
         *     `type`, `address` and `position`.
         * @param {String} [type="resultsDrawn"] Event's name
         * @param {Element} target Event's target
         *
         * @example
         * ```javascript
         * var widget = new L.SearchBox();
         * widget.addTo(map);
         * widget.on(L.SearchBox.Events.ResultsDrawn, function(eventObject) {
         *   console.log(eventObject.data);
         * });
         * ```
         */
        /**
         * Event that is fired when the response from search service appears for both normal and prompt query.
         *
         * @event ResultsFound
         * @param {Array} data Array of search service results. Each result has, among other parameters, its
         *     `type`, `address` and `position`.
         * @param {String} [type="resultsFound"] Event's name
         * @param {Element} target Event's target
         *
         * @example
         * ```javascript
         * var widget = new L.SearchBox();
         * widget.addTo(map);
         * widget.on(L.SearchBox.Events.ResultsFound, function(eventObject) {
         *   console.log(eventObject.data);
         * });
         * ```
         */
        /**
         * Event that is fired when user clicks the clear search box input button.
         *
         * @event ClearButtonClicked
         * @param {String} [type="clearButtonClicked"] Event's name
         * @param {Element} target Event's target
         *
         * @example
         * ```javascript
         * var widget = new L.SearchBox();
         * widget.addTo(map);
         * widget.on(L.SearchBox.Events.ClearButtonClicked, function() {
         *   console.log("User clicked the clear button");
         * });
         * ```
         */
        /**
         * Event that is fired when prompt results list is being cleared.
         *
         * @event ResultsCleared
         * @param {String} [type="resultsCleared"] Event's name
         * @param {Element} target Event's target
         *
         * @example
         * ```javascript
         * var widget = new L.SearchBox();
         * widget.addTo(map);
         * widget.on(L.SearchBox.Events.LocationsCleared, function() {
         *   console.log("Results were cleared");
         * });
         * ```
         */
        /**
         * Event that is fired when obtaining user's current location fails.
         *
         * @event LocationError
         * @param {Object} eventObject All failure information
         * @param {String} [type="locationError"] Event's name
         * @param {Element} target Event's target
         *
         * @example
         * ```javascript
         * var widget = new L.SearchBox();
         * widget.addTo(map);
         * widget.on(L.SearchBox.Events.LocationError, function(eventObject) {
         *   console.log(eventObject);
         * });
         * ```
         */
        Events: EVENTS
    });

    return function(options) {
        var searchBox = new L.SearchBox(options);
        utils.addDeprecatedProperty(searchBox, 'Event', 'Events');
        return searchBox;
    };
});



// WEBPACK FOOTER //
// ./widgets/controls/searchBox.js